import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { QrCodeIcon, RotateCwIcon, SparklesIcon } from 'lucide-react';
import { petReminders, sourceLabels, type Pet } from '../../data/animaux';
interface PetCardProps {
  pet: Pet;
  lost: boolean;
}
export function PetCard({
  pet,
  lost
}: PetCardProps) {
  const [flipped, setFlipped] = useState(false);
  const reminders = petReminders[pet.id] ?? [];
  return <div data-id="element-219" data-mp="3ghzsjeylhkcj">
      <div className="[perspective:1600px]" data-id="element-220" data-mp="3gi84n4mw8lmo">
        <motion.div animate={{
        rotateY: flipped ? 180 : 0
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative h-[420px] w-full [transform-style:preserve-3d]" data-id="element-221" data-mp="3lg1dk5fkztqn">
          {/* Front */}
          <div className="absolute inset-0 overflow-hidden rounded-3xl bg-ville-navy text-white shadow-2xl shadow-ville-navy/25 [backface-visibility:hidden]" data-id="element-222" data-mp="3u94x8vgtl2tq">
            <div className="flex h-full" data-id="element-223" data-mp="31rsm5xlv3rs7">
              <img src={pet.photo} alt={pet.name} className="h-full w-2/5 object-cover" data-id="element-224" data-mp="31qhh7ugokdzi" />
              <div className="flex flex-1 flex-col p-6" data-id="element-225" data-mp="jv1s4rxvl9vq">
                <div className="flex items-start justify-between gap-3" data-id="element-226" data-mp="exidvs16gx3s">
                  <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-white/60" data-id="element-227" data-mp="ezcyf58jen7c">
                    Carte animale
                  </span>
                  {lost ? <span className="rounded-full bg-red-600 px-3 py-1 text-xs font-bold uppercase tracking-wider" data-id="element-228" data-mp="576ztcns57j6">
                      Perdu
                    </span> : <span className="rounded-full bg-ville-lime px-3 py-1 text-xs font-bold text-ville-navy" data-id="element-229" data-mp="3sav732ck6dao">
                      Licence valide
                    </span>}
                </div>

                <p className="mt-5 text-3xl font-extrabold tracking-tight" data-id="element-230" data-mp="3ig38bv78pkeb">{pet.name}</p>
                <p className="text-sm text-white/70" data-id="element-231" data-mp="1lkxd9a0sh85h">
                  {pet.breed} · {pet.age}
                </p>

                <dl className="mt-6 space-y-2 text-sm" data-id="element-232" data-mp="1gkszhrr9mriy">
                  <div className="flex justify-between gap-3" data-id="element-233" data-mp="3b22g06s2sqsc">
                    <dt className="text-white/55" data-id="element-234" data-mp="3fu2leeqx0bwb">Naissance</dt>
                    <dd className="font-semibold" data-id="element-235" data-mp="3fvnhi8svo2wa">{pet.birthDate}</dd>
                  </div>
                  <div className="flex justify-between gap-3" data-id="element-236" data-mp="316hfye6ki0l8">
                    <dt className="text-white/55" data-id="element-237" data-mp="364srqugfal63">Médaille</dt>
                    <dd className="font-mono tracking-widest" data-id="element-238" data-mp="3ttbyh5jtqpw2">{pet.medal}</dd>
                  </div>
                  <div className="flex justify-between gap-3" data-id="element-239" data-mp="2mr359mqkvt8">
                    <dt className="text-white/55" data-id="element-240" data-mp="2m998jnjqtoh">Validité</dt>
                    <dd className="font-semibold" data-id="element-241" data-mp="3jvxnvmkl0vbo">{pet.validUntil}</dd>
                  </div>
                  <div className="flex justify-between gap-3" data-id="element-242" data-mp="3otfv98iyb2hk">
                    <dt className="text-white/55" data-id="element-243" data-mp="3ow20v2ma77x5">Gardien</dt>
                    <dd className="font-semibold" data-id="element-244" data-mp="28efpz3pompx2">{pet.owner}</dd>
                  </div>
                </dl>

                <div className="mt-auto flex items-end justify-between" data-id="element-245" data-mp="2dgdfugsl8iyz">
                  <span className="text-xs font-semibold text-white/60" data-id="element-246" data-mp="vge1box3l2ib">Ville de Brossard</span>
                  <span className="grid h-12 w-12 place-items-center rounded-xl bg-white/10" aria-hidden="true" data-id="element-247" data-mp="qhazsegbtef3">
                    <QrCodeIcon className="h-7 w-7 text-white" data-id="element-248" data-mp="14hlpvf9taoz7" />
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Back */}
          <div className="absolute inset-0 overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 shadow-xl [backface-visibility:hidden] [transform:rotateY(180deg)]" data-id="element-249" data-mp="cs0rp9iqjcht">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-ville-indigo" data-id="element-250" data-mp="ljsztdkae9j8">À savoir</p>
            <h3 className="mt-1 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-251" data-mp="gsaw4hqvpuuk">{pet.name}</h3>

            <ul className="mt-5 space-y-3" data-id="element-252" data-mp="3p7kupfvk8hxl">
              {reminders.map(reminder => <li key={reminder.id} className="rounded-2xl bg-slate-50 px-4 py-3" data-id="element-253" data-mp="3l7khtzo40esn">
                  <div className="flex flex-wrap items-baseline justify-between gap-2" data-id="element-254" data-mp="1qw03hnabnhw">
                    <p className="text-sm font-bold text-ville-navy" data-id="element-255" data-mp="6pp48jgom25e">{reminder.label}</p>
                    <p className="text-sm text-slate-600" data-id="element-256" data-mp="6ncl42z6tlqr">{reminder.date}</p>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500" data-id="element-257" data-mp="bmz3eqtvhs6f">{sourceLabels[reminder.source]}</p>
                </li>)}
              <li className="rounded-2xl bg-slate-50 px-4 py-3" data-id="element-258" data-mp="lfd4rih1gws2">
                <div className="flex flex-wrap items-baseline justify-between gap-2" data-id="element-259" data-mp="pg6saaqkk9kf">
                  <p className="text-sm font-bold text-ville-navy" data-id="element-260" data-mp="pi9pbeq532yn">Micropuce</p>
                  <p className="font-mono text-sm text-slate-600" data-id="element-261" data-mp="uft90u5lw7xz">{pet.microchip}</p>
                </div>
              </li>
            </ul>

            <p className="mt-5 flex items-center gap-2 text-sm font-bold text-ville-indigo" data-id="element-262" data-mp="2w6wgxbo438eu">
              <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-263" data-mp="315pypgafllkt" />
              Demander à ATNA
            </p>
            <p className="mt-1 text-xs leading-relaxed text-slate-500" data-id="element-264" data-mp="2tmqdr67bnlzc">
              bciti+ n’est pas le dossier médical officiel de votre animal. Les informations proviennent de vos rappels,
              de la Ville ou de votre clinique autorisée.
            </p>
          </div>
        </motion.div>
      </div>

      <button type="button" onClick={() => setFlipped(v => !v)} className="mt-4 inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-265" data-mp="2ey3q8h7jjnx1">
        <RotateCwIcon className="h-4 w-4" aria-hidden="true" data-id="element-266" data-mp="2xoldi69ltrb0" />
        {flipped ? 'Voir la carte' : 'Retourner la carte'}
      </button>
    </div>;
}